import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
	getPipelineTypes,
	startPipeline,
	getPipelineJob,
	proceedPipeline,
	regenerateStep,
	selectStepResult,
	retryPipeline,
	stepEffectiveCost,
	type PipelineJob,
	type PipelineTypeConfig,
	type PipelineStep,
	type PipelineStepAttempt,
} from '../../../api/pipelines'

/**
 * usePipelineFlow — многоэтапный режим GTM для пайплайн-карточек (тренды
 * «фото → видео»). Оркеструет job бэка (/api/v1/pipelines): старт с фото
 * юзера, поллинг, версии шага («сгенерировать ещё» копит attempts, не
 * затирает), выбор версии, видео из выбранной, повторные видео.
 *
 * Job переживает F5: id хранится в sessionStorage per-slug (как пин) и
 * восстанавливается getPipelineJob — юзер возвращается к своим версиям.
 * Этапы платные по отдельности: цена шага — credits_estimate шаблона либо
 * промо discount_price при живой акции (stepEffectiveCost, зеркало бэка).
 */

export type PipelinePhase =
	| 'idle'
	| 'photo_processing'
	| 'photo_ready'
	| 'video_processing'
	| 'video_ready'
	| 'failed'

const POLL_MS = 4000
/** Ключ живого job'а прогона. Экспортирован: по нему внешние места (см.
 *  bots/utils/activeToolRun) понимают, что работа ещё не закрыта. */
export const pipelineJobStorageKey = (slug: string) => `umnik.pipeline_job.${slug}`
const storageKey = pipelineJobStorageKey

// Стабилизация presigned-URL при обновлении job. Бэк (_rehydrate_result_urls)
// пере-подписывает ссылки на КАЖДЫЙ ответ /job — подпись (X-Amz-*) меняется, и
// при поллинге (каждые 4с) <img src> миниатюр версий менялся → браузер их
// перезагружал (мигание во время генерации видео). Если путь файла (URL без
// query-подписи) не изменился — оставляем ПРЕЖНЮЮ строку URL: тот же файл,
// src стабилен, рефетча нет. Новый путь (свежий вариант) берёт свежий URL;
// после F5 (prev = null) — тоже свежие (там как раз нужны валидные подписи).
const pathOf = (u?: string) => (u ? u.split('?')[0] : '')
function stabilizeJobUrls(prev: PipelineJob | null, next: PipelineJob): PipelineJob {
	if (!prev) return next
	const seen = new Map<string, string>()
	const remember = (u?: string) => {
		if (u && u.includes('?')) seen.set(pathOf(u), u)
	}
	for (const s of prev.steps ?? []) {
		for (const a of s.attempts ?? []) remember(a.result_url)
		remember(s.output?.result_url)
	}
	const keep = (u?: string) => (u && u.includes('?') && seen.has(pathOf(u)) ? seen.get(pathOf(u))! : u)
	const steps = (next.steps ?? []).map((s) => ({
		...s,
		attempts: (s.attempts ?? []).map((a) => ({ ...a, result_url: keep(a.result_url) as string })),
		output: s.output ? { ...s.output, result_url: keep(s.output?.result_url) } : s.output,
	}))
	return { ...next, steps }
}

export function usePipelineFlow(slug: string | undefined) {
	const [cfg, setCfg] = useState<PipelineTypeConfig | null>(null)
	const [job, setJob] = useState<PipelineJob | null>(null)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState('')
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

	// Конфиг шаблона (шаги/цены/схема входа) — общий публичный эндпоинт.
	useEffect(() => {
		if (!slug) return
		let alive = true
		getPipelineTypes()
			.then((types) => {
				if (!alive) return
				setCfg(types[slug] || types[slug.replace(/-/g, '_')] || null)
			})
			.catch(() => {})
		return () => {
			alive = false
		}
	}, [slug])

	// Индексы этапов: первый image-шаг = «фото», первый video-шаг = «видео».
	const imageStepIdx = useMemo(
		() => (cfg ? cfg.steps.findIndex((s) => s.type === 'image') : -1),
		[cfg],
	)
	const videoStepIdx = useMemo(
		() => (cfg ? cfg.steps.findIndex((s) => s.type === 'video') : -1),
		[cfg],
	)
	const photoField = useMemo(
		() => cfg?.input_schema?.fields.find((f) => f.type === 'image_upload') ?? null,
		[cfg],
	)

	const stopPolling = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current)
			pollRef.current = null
		}
	}, [])
	useEffect(() => () => stopPolling(), [stopPolling])

	const applyJob = useCallback(
		(next: PipelineJob | null) => {
			setJob((prev) => (next ? stabilizeJobUrls(prev, next) : null))
			if (!slug) return
			try {
				if (next) sessionStorage.setItem(storageKey(slug), next.id)
				else sessionStorage.removeItem(storageKey(slug))
			} catch {
				/* quota — job проживёт до F5 */
			}
		},
		[slug],
	)

	const startPolling = useCallback(
		(jobId: string) => {
			stopPolling()
			pollRef.current = setInterval(async () => {
				try {
					const res = await getPipelineJob(jobId)
					if (res.ok && res.job) {
						const fresh = res.job
						setJob((prev) => stabilizeJobUrls(prev, fresh))
						const cur = fresh.steps[fresh.current_step]
						if (!cur || cur.status !== 'processing') stopPolling()
					}
				} catch {
					/* тик поллинга повторит */
				}
			}, POLL_MS)
		},
		[stopPolling],
	)

	// Восстановление job после F5 (пин пережил — этапы тоже должны).
	// Смена карточки (slug) на лету: компонент GTM НЕ ремоунтится — прошлый
	// job сбрасываем сразу, иначе версии чужого тренда мигали бы в новой.
	useEffect(() => {
		stopPolling()
		setJob(null)
		setError('')
		if (!slug) return
		let saved: string | null = null
		try {
			saved = sessionStorage.getItem(storageKey(slug))
		} catch {
			saved = null
		}
		if (!saved) return
		getPipelineJob(saved)
			.then((res) => {
				if (res.ok && res.job && res.job.status !== 'cancelled') {
					setJob(res.job)
					const cur = res.job.steps[res.job.current_step]
					if (cur?.status === 'processing') startPolling(res.job.id)
				}
			})
			.catch(() => {})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [slug])

	const run = useCallback(
		async (fn: () => Promise<{ ok: boolean; job: PipelineJob }>) => {
			setBusy(true)
			setError('')
			try {
				const res = await fn()
				if (res.ok && res.job) {
					applyJob(res.job)
					const cur = res.job.steps[res.job.current_step]
					if (cur?.status === 'processing') startPolling(res.job.id)
				} else {
					setError((res as { error?: string }).error || 'Ошибка')
				}
			} catch (e) {
				setError(e instanceof Error ? e.message : 'Ошибка')
			} finally {
				setBusy(false)
			}
		},
		[applyJob, startPolling],
	)

	/** Полный сброс прогона («начать заново»): job, sessionStorage, ошибка.
	 *  Сгенерированное не пропадает — версии уже лежат в «Моих генерациях». */
	const resetJob = useCallback(() => {
		stopPolling()
		applyJob(null)
		setError('')
	}, [stopPolling, applyJob])

	/** Этап «Фото»: первый запуск создаёт job, дальше — «сгенерировать ещё».
	 *  Вход job'а (config.user_photos) ЗАМОРОЖЕН при создании — regenerate берёт
	 *  его, не текущий слот. Поэтому если фото в слоте отличаются от тех, из
	 *  которых собран job (сменил фото / job воскрес из sessionStorage после
	 *  закрытия) → это ДРУГОЙ вход: сбрасываем старый job и стартуем новый.
	 *  Совпадают → regenerate («ещё вариант» из того же набора). */
	const createPhoto = useCallback(
		(photoUrls: string[], character?: { id: string; name: string; urls: string[] } | null) => {
			if (!slug) return
			const key = photoField?.key || 'user_photos'
			const jobPhotos = Array.isArray(job?.config?.[key]) ? (job!.config[key] as string[]) : []
			const sameInput =
				!!job &&
				photoUrls.length === jobPhotos.length &&
				photoUrls.every((u, i) => u === jobPhotos[i])
			// Новый вход (нет job, или слот ≠ config при непустом слоте) → свежий
			// job. Пустой слот при живом job (F5 очистил локальные фото) — это
			// «ещё» из config, не пересоздаём.
			if (!job || (photoUrls.length > 0 && !sameInput)) {
				// Slug — КАК ЕСТЬ: бэк резолвит шаблон точным совпадением
				// (`WHERE slug = :slug`). Замена дефисов на подчёркивания
				// (реликт легаси-конфигов старого раннера) ломала авто-слаги
				// карточек `card-<id>` → «Unknown pipeline type».
				if (job) resetJob() // старый job/версии в ноль — вход сменился
				// Атрибуция персонажа (шаг 6) — в config job'а (JSONB, бэк пасс-тру):
				// character_photo_urls нужны restore-эффекту после F5 (какие из
				// user_photos — персонажа); rehydrate бэка пере-подпишет их свежими
				// заодно с user_photos (не-URL строки не трогает).
				const attribution = character
					? {
							character_id: character.id,
							character_name: character.name,
							character_photo_urls: character.urls,
						}
					: {}
				void run(() => startPipeline(slug, { [key]: photoUrls, ...attribution }))
			} else {
				void run(() => regenerateStep(job.id, imageStepIdx))
			}
		},
		[slug, job, photoField, imageStepIdx, run, resetJob],
	)

	/** Этап «Видео»: первый раз — proceed (маппинг возьмёт выбранное фото),
	 *  повторно — regenerate video-шага (версии видео тоже копятся). */
	const createVideo = useCallback(() => {
		if (!job || videoStepIdx < 0) return
		const videoStep = job.steps[videoStepIdx]
		if (job.current_step < videoStepIdx && (!videoStep || videoStep.status === 'pending')) {
			void run(() => proceedPipeline(job.id))
		} else {
			void run(() => regenerateStep(job.id, videoStepIdx))
		}
	}, [job, videoStepIdx, run])

	/** Выбор версии фото (из какой генерить видео). Оптимистично: выбор виден
	 *  сразу, бэк подтверждает следом. */
	const choosePhoto = useCallback(
		(url: string) => {
			if (!job || imageStepIdx < 0) return
			setJob((prev) => {
				if (!prev) return prev
				const steps = prev.steps.map((s, i) =>
					i === imageStepIdx ? { ...s, output: { ...s.output, result_url: url } } : s,
				)
				return { ...prev, steps }
			})
			selectStepResult(job.id, imageStepIdx, url)
				.then((res) => {
					if (res.ok && res.job) {
						const fresh = res.job
						setJob((prev) => stabilizeJobUrls(prev, fresh))
					}
				})
				.catch(() => {})
		},
		[job, imageStepIdx],
	)

	const retryFailed = useCallback(() => {
		if (!job) return
		void run(() => retryPipeline(job.id))
	}, [job, run])

	// ── Derived state для рендера ────────────────────────────────────
	const photoStep: PipelineStep | null = job && imageStepIdx >= 0 ? job.steps[imageStepIdx] : null
	const videoStep: PipelineStep | null = job && videoStepIdx >= 0 ? job.steps[videoStepIdx] : null
	const photoAttempts: PipelineStepAttempt[] = photoStep?.attempts ?? []
	const videoAttempts: PipelineStepAttempt[] = videoStep?.attempts ?? []
	const selectedPhotoUrl: string | null = photoStep?.output?.result_url ?? null
	const videoUrl: string | null = videoStep?.output?.result_url ?? null

	let phase: PipelinePhase = 'idle'
	if (job) {
		const curStep = job.steps[job.current_step]
		if (job.status === 'failed' || curStep?.status === 'failed') phase = 'failed'
		else if (videoStep?.status === 'processing') phase = 'video_processing'
		else if (photoStep?.status === 'processing' || photoStep?.status === 'pending') phase = 'photo_processing'
		else if (videoStep?.status === 'completed' && videoUrl && job.current_step >= videoStepIdx)
			phase = 'video_ready'
		else if (photoStep?.status === 'completed') phase = 'photo_ready'
	}

	// Цена этапа = то, что реально спишет бэк: промо при живой акции, иначе
	// базовая цена шаблона (единая точка — stepEffectiveCost).
	const photoCost = imageStepIdx >= 0 ? stepEffectiveCost(cfg?.steps[imageStepIdx]) : 0
	const videoCost = videoStepIdx >= 0 ? stepEffectiveCost(cfg?.steps[videoStepIdx]) : 0

	// Формат этапов — рамка результата виджета подстраивается под него (иначе
	// брала глобальный дефолт gen.settings, letterbox'я 9:16-контент).
	const photoAspect = imageStepIdx >= 0 ? cfg?.steps[imageStepIdx]?.aspect_ratio : undefined
	const videoAspect = videoStepIdx >= 0 ? cfg?.steps[videoStepIdx]?.aspect_ratio : undefined

	return {
		cfg,
		job,
		busy,
		error,
		phase,
		photoField,
		photoStep,
		videoStep,
		photoAttempts,
		videoAttempts,
		selectedPhotoUrl,
		videoUrl,
		photoCost,
		videoCost,
		photoAspect,
		videoAspect,
		createPhoto,
		createVideo,
		choosePhoto,
		retryFailed,
		resetJob,
	}
}
